import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { AD_CONFIG } from '../../config/adConfig';
import AdManager from './AdManager';

/**
 * Google AdSense Banner Component
 * Dynamically loads and displays Google AdSense ads
 * 
 * @param {string} slot - AdSense slot ID
 * @param {string} format - Ad format (auto, horizontal, rectangle, vertical, fluid)
 * @param {boolean} responsive - Enable responsive ads
 * @param {object} style - Custom styles for ad container
 * @param {string} className - Additional CSS classes
 */
const AdSenseBanner = ({ 
  slot, 
  format = 'auto', 
  responsive = true,
  style = {},
  className = '',
  ...props 
}) => {
  const adRef = useRef(null);
  const adLoadedRef = useRef(false);

  useEffect(() => {
    // Only load ad once per element and avoid running in local development or when an ad is already present
    if (adLoadedRef.current) return;

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (process.env.NODE_ENV === 'development' || isLocalhost) {
      // Do not attempt to load real ads when developing locally
      return () => {};
    }

    try {
      if (!adRef.current) return () => {};

      // If the ins already has an iframe or status 'done', skip pushing
      const hasIframe = adRef.current.querySelector && adRef.current.querySelector('iframe');
      const status = adRef.current.getAttribute && adRef.current.getAttribute('data-adsbygoogle-status');
      if (hasIframe || status === 'done') {
        adLoadedRef.current = true;
        return () => {};
      }

      // If script isn't loaded but user consent exists, load it dynamically
      const consent = typeof window !== 'undefined' ? localStorage.getItem('ads_consent') : null;
      const client = AD_CONFIG.googleAdSense?.client;
      if (!window.adsbygoogle && client && consent === 'granted') {
        AdManager.loadAdSense(client).then(() => {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            adLoadedRef.current = true;
          } catch (e) {
            console.error('Ad push after dynamic load failed', e);
          }
        }).catch((e) => {
          console.error('Failed to load AdSense script', e);
        });
        return () => {};
      }

      if (window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        adLoadedRef.current = true;
      }
    } catch (error) {
      // AdSense library throws if push is called multiple times; catch and log but don't break the app
      console.error('AdSense error:', error?.message || error);
    }

    return () => {
      adLoadedRef.current = false;
    };
  }, []);

  // Development fallback - show placeholder text
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  if (!AD_CONFIG.enabled || !AD_CONFIG.googleAdSense?.client) {
    if (isDevelopment) {
      return (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 90,
            bgcolor: 'grey.100',
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            ...style,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Ad Space ({format})
          </Typography>
        </Paper>
      );
    }
    return null; // Production - show nothing
  }

  return (
    <Box
      className={`ad-container ${className}`}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
        ...style,
      }}
      {...props}
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
        data-ad-client={AD_CONFIG.googleAdSense.client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </Box>
  );
};

export default AdSenseBanner;
